import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  Plus, Package, Building2, UserCheck, BarChart3, Eye, Trash2,
  Pencil, RefreshCw, Search, AlertTriangle, BellOff, ClipboardList, PackageMinus,
  Truck, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "../components/SignaturePad";
import HorizontalScrollContainer from "../components/HorizontalScrollContainer";
import EquipmentRequestsTab from "../components/EquipmentRequestsTab";
import EquipmentReturnsTab from "../components/EquipmentReturnsTab";
import MenuBadge from "../components/MenuBadge";
import StickyPageHeader from "../components/StickyPageHeader";
import { useNotificationCounts } from "../contexts/NotificationCountsContext";
import { formatDateFr, formatDateTimeFr, formatTimeFr } from "../lib/dateFormat";

const ETATS = ["Neuf", "Bon état", "État d'usage", "Endommagé"];

const DELIVERY_MODE_LABELS = {
  presence_sur_site: "Immédiatement sur site",
  a_acheminer_sur_site: "À acheminer sur site",
};

const COLLAB_DELIVERY_MODE_LABELS = {
  remis_en_main_propre: "Remis en main propre",
  acheminement_livraison: "Acheminement livraison",
};

function filterCollabsHelper(collabs, search) {
  const q = (search || "").toLowerCase().trim();
  const list = (collabs || []).filter((c) => c.role !== "admin");
  if (!q) return list;
  return list.filter((c) => (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q));
}

const ETAT_COLOR = {
  "Neuf": "bg-emerald-50 border-emerald-200 text-emerald-700",
  "Bon état": "bg-sky-50 border-sky-200 text-sky-700",
  "État d'usage": "bg-amber-50 border-amber-200 text-amber-700",
  "Endommagé": "bg-red-50 border-red-200 text-red-700",
};

function EtatBadge({ value }) {
  const cls = ETAT_COLOR[value] || "bg-zinc-50 border-zinc-200 text-zinc-700";
  return (
    <span className={`text-[10px] uppercase font-semibold px-2 py-1 border rounded-sm tracking-wider ${cls}`}>
      {value || "—"}
    </span>
  );
}

function SignatureViewer({ url, label = "Voir la signature" }) {
  const [open, setOpen] = useState(false);
  if (!url) return <span className="text-xs text-zinc-400 font-mono">—</span>;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 px-2" data-testid="view-signature-btn">
          <Eye size={12} className="mr-1" /> Voir
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-center p-4 border border-zinc-200 rounded-sm bg-white">
          <img src={url} alt="Signature" className="max-h-60 object-contain" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Barre de recherche réutilisable (avec filtres optionnels). */
function FiltersBar({ search, onSearchChange, brand, onBrandChange, brands, etat, onEtatChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap items-end gap-2 bg-white border border-zinc-200 rounded-sm p-3">
      <div className="flex-1 min-w-[200px]">
        <Label className="label-uppercase mb-1 block">Recherche</Label>
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input
            className="pl-7"
            placeholder="Désignation, référence, collaborateur, site…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            data-testid={`${testIdPrefix}-search`}
          />
        </div>
      </div>
      {brands && (
        <div className="min-w-[170px]">
          <Label className="label-uppercase mb-1 block">Département</Label>
          <Select value={brand || "__all"} onValueChange={(v) => onBrandChange(v === "__all" ? "" : v)}>
            <SelectTrigger data-testid={`${testIdPrefix}-brand-filter`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tous</SelectItem>
              {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {onEtatChange && (
        <div className="min-w-[150px]">
          <Label className="label-uppercase mb-1 block">État</Label>
          <Select value={etat || "__all"} onValueChange={(v) => onEtatChange(v === "__all" ? "" : v)}>
            <SelectTrigger data-testid={`${testIdPrefix}-etat-filter`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tous</SelectItem>
              {ETATS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

export default function Equipements() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const { byPageTab: unreadByTab } = useNotificationCounts();
  const [entries, setEntries] = useState([]);
  const [siteAssigns, setSiteAssigns] = useState([]);
  const [collabAssigns, setCollabAssigns] = useState([]);
  const [stock, setStock] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [sites, setSites] = useState([]);
  const [collabs, setCollabs] = useState([]);

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isGest = user?.role === "gestionnaire";
  const canCreateEntry = isAdmin || isManager || isGest;
  const canAssign = isAdmin || isManager;
  const canSeeAlerts = isAdmin || isManager || isGest;
  const allowed = !user || isAdmin || isManager || isGest;

  // Map id -> disponible (snapshot live)
  const stockMap = useMemo(() => {
    const m = {};
    (stock || []).forEach((s) => { m[s.id] = s; });
    return m;
  }, [stock]);

  const tabs = useMemo(() => {
    const t = [{ value: "entrees", label: "Entrées stock", icon: Package }];
    if (canAssign) {
      t.push({ value: "sites", label: "Affectations sites", icon: Building2 });
      t.push({ value: "collabs", label: "Affectations collaborateurs", icon: UserCheck });
      t.push({ value: "requests", label: "Demandes de matériels", icon: ClipboardList });
      t.push({ value: "returns",  label: "Restitutions de matériels", icon: PackageMinus });
    }
    t.push({ value: "stock", label: "Stock disponible", icon: BarChart3 });
    if (canSeeAlerts) {
      t.push({ value: "alerts", label: "Alerte stock", icon: AlertTriangle, danger: true });
    }
    return t;
  }, [canAssign, canSeeAlerts]);

  const load = async () => {
    const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
    const reqs = [api.get("/equipment", { params }), api.get("/equipment/stock", { params })];
    if (canAssign) {
      reqs.push(api.get("/equipment-site-assignments", { params }));
      reqs.push(api.get("/equipment-collab-assignments", { params }));
      reqs.push(api.get("/sites", { params }));
      reqs.push(api.get("/collaborateurs", { params }));
    }
    if (canSeeAlerts) {
      reqs.push(api.get("/equipment/alerts", { params }));
    }
    try {
      const res = await Promise.all(reqs);
      setEntries(res[0].data || []);
      setStock(res[1].data || []);
      let idx = 2;
      if (canAssign) {
        setSiteAssigns(res[idx++].data || []);
        setCollabAssigns(res[idx++].data || []);
        setSites(res[idx++].data || []);
        setCollabs(res[idx++].data || []);
      }
      if (canSeeAlerts) {
        setAlerts(res[idx++].data || []);
      }
    } catch (e) {
      toast.error("Erreur lors du chargement");
    }
  };

  useEffect(() => {
    // eslint-disable-next-line
    if (allowed) load();
  }, [allowed, selectedBrandId]);

  // Synchronisation temps réel : recharger les affectations dès qu'un événement
  // matériel survient (demande, décision, restitution signée, contre-signée…)
  useEffect(() => {
    if (!allowed) return;
    const onNotif = (ev) => {
      const t = ev?.detail?.type || "";
      if (
        t === "equipment_request"
        || t === "equipment_request_decision"
        || t === "equipment_return_requested"
        || t === "equipment_return_collab_signed"
        || t === "equipment_return_validated"
        || t === "equipment_return_rejected"
        || t === "equipment_return_rejected_by_controller"
        || t === "equipment_assignment"
      ) {
        load();
      }
    };
    const onReloadAssign = () => load();
    window.addEventListener("plania:notification", onNotif);
    window.addEventListener("plania:equipment-returns-updated", onReloadAssign);
    window.addEventListener("plania:equipment-requests-updated", onReloadAssign);
    window.addEventListener("plania:equipment-assignments-updated", onReloadAssign);
    return () => {
      window.removeEventListener("plania:notification", onNotif);
      window.removeEventListener("plania:equipment-returns-updated", onReloadAssign);
      window.removeEventListener("plania:equipment-requests-updated", onReloadAssign);
      window.removeEventListener("plania:equipment-assignments-updated", onReloadAssign);
    };
  }, [allowed]);

  if (!allowed) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6" data-testid="equipements-page">
      <Tabs defaultValue="entrees" className="w-full">
        <StickyPageHeader testid="equipements-sticky-header">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="label-uppercase mb-2">Logistique</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900">
              Matériels &amp; équipements
            </h1>
            <p className="text-sm text-zinc-600 mt-1 font-mono">
              Entrées, affectations, stock disponible et alertes de réapprovisionnement
            </p>
          </div>
        </div>

        <div className="w-full overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="bg-white border border-zinc-200 rounded-sm p-0 h-auto w-max">
            {tabs.map((t) => {
              const Icon = t.icon;
              const isDanger = t.danger;
              const badgeCount = unreadByTab[`equipements.${t.value}`] || 0;
              return (
                <TabsTrigger
                  key={t.value}
                  value={t.value}
                  className={`rounded-sm px-3 py-2 data-[state=active]:bg-zinc-100 ${isDanger ? "data-[state=active]:bg-red-50" : ""}`}
                  data-testid={`tab-equip-${t.value}`}
                >
                  <Icon size={13} className={`mr-1.5 ${isDanger ? "text-red-600" : ""}`} />
                  <span className={isDanger ? "text-red-700" : ""}>{t.label}</span>
                  {isDanger && alerts.length > 0 && (
                    <span className="ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-red-100 text-red-800 border border-red-300">
                      {alerts.length}
                    </span>
                  )}
                  <MenuBadge
                    count={badgeCount}
                    size="xs"
                    className="ml-1.5"
                    testid={`tab-badge-equip-${t.value}`}
                  />
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>
        </StickyPageHeader>

        <TabsContent value="entrees" className="mt-6">
          <EntriesTab
            entries={entries}
            brands={brands}
            user={user}
            canCreateEntry={canCreateEntry}
            canAssign={canAssign}
            isAdmin={isAdmin}
            stockMap={stockMap}
            reload={load}
          />
        </TabsContent>

        {canAssign && (
          <TabsContent value="sites" className="mt-6">
            <SiteAssignmentsTab
              items={siteAssigns}
              entries={entries}
              stock={stock}
              stockMap={stockMap}
              brands={brands}
              sites={sites}
              collabs={collabs}
              user={user}
              isAdmin={isAdmin}
              reload={load}
            />
          </TabsContent>
        )}

        {canAssign && (
          <TabsContent value="collabs" className="mt-6">
            <CollabAssignmentsTab
              items={collabAssigns}
              entries={entries}
              stock={stock}
              stockMap={stockMap}
              collabs={collabs}
              brands={brands}
              user={user}
              isAdmin={isAdmin}
              reload={load}
            />
          </TabsContent>
        )}

        {canAssign && (
          <TabsContent value="requests" className="mt-6">
            <EquipmentRequestsTab />
          </TabsContent>
        )}

        {canAssign && (
          <TabsContent value="returns" className="mt-6">
            <EquipmentReturnsTab />
          </TabsContent>
        )}

        <TabsContent value="stock" className="mt-6">
          <StockTab stock={stock} brands={brands} />
        </TabsContent>

        {canSeeAlerts && (
          <TabsContent value="alerts" className="mt-6">
            <AlertsTab alerts={alerts} brands={brands} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/* ---------------- Entries Tab ---------------- */

function EntriesTab({ entries, brands, user, canCreateEntry, canAssign, isAdmin, stockMap, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);     // entry à modifier
  const [restocking, setRestocking] = useState(null); // entry à réapprovisionner
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [etatFilter, setEtatFilter] = useState("");
  const defaultBrand = (user?.brand_ids && user.brand_ids[0]) || "";
  const [form, setForm] = useState({
    designation: "", ref_id: "", caracteristiques: "", etat: "Neuf",
    brand_id: defaultBrand, date_entree: new Date().toISOString().slice(0, 10),
    quantite: 1, controller_signature: null, alert_enabled: true,
  });

  const allowedBrands = user?.role === "admin" ? brands : brands.filter((b) => (user?.brand_ids || []).includes(b.id));

  const resetForm = () => setForm({
    designation: "", ref_id: "", caracteristiques: "", etat: "Neuf",
    brand_id: defaultBrand, date_entree: new Date().toISOString().slice(0, 10),
    quantite: 1, controller_signature: null, alert_enabled: true,
  });

  const submit = async () => {
    if (!form.designation.trim() || !form.ref_id.trim() || !form.brand_id || !form.date_entree) {
      toast.error("Désignation, référence, département et date requis");
      return;
    }
    if (!form.controller_signature) {
      toast.error("Veuillez apposer votre signature");
      return;
    }
    try {
      await api.post("/equipment", { ...form, quantite: Number(form.quantite) });
      toast.success("Entrée enregistrée");
      setOpen(false);
      resetForm();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette entrée ?")) return;
    try {
      await api.delete(`/equipment/${id}`);
      toast.success("Supprimée");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // Filtrage
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return entries.filter((e) => {
      if (brandFilter && e.brand_id !== brandFilter) return false;
      if (etatFilter && e.etat !== etatFilter) return false;
      if (!q) return true;
      return (
        (e.designation || "").toLowerCase().includes(q)
        || (e.ref_id || "").toLowerCase().includes(q)
        || (e.caracteristiques || "").toLowerCase().includes(q)
      );
    });
  }, [entries, search, brandFilter, etatFilter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltersBar
          search={search} onSearchChange={setSearch}
          brand={brandFilter} onBrandChange={setBrandFilter} brands={allowedBrands}
          etat={etatFilter} onEtatChange={setEtatFilter}
          testIdPrefix="entries"
        />
        {canCreateEntry && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm shrink-0" data-testid="new-equipment-btn">
                <Plus size={14} className="mr-1" /> Nouvelle entrée
              </Button>
            </DialogTrigger>
            <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-2xl">
              <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
                <DialogTitle>Nouvelle entrée stock</DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Désignation</Label>
                    <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="eq-designation" />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">N° / Référence</Label>
                    <Input value={form.ref_id} onChange={(e) => setForm({ ...form, ref_id: e.target.value })} data-testid="eq-ref" />
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Caractéristiques</Label>
                  <Textarea rows={2} value={form.caracteristiques} onChange={(e) => setForm({ ...form, caracteristiques: e.target.value })} placeholder="Couleur, matière, autres détails…" data-testid="eq-caract" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">État</Label>
                    <Select value={form.etat} onValueChange={(v) => setForm({ ...form, etat: v })}>
                      <SelectTrigger data-testid="eq-etat"><SelectValue /></SelectTrigger>
                      <SelectContent>{ETATS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Département</Label>
                    <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                      <SelectTrigger data-testid="eq-brand"><SelectValue placeholder="…" /></SelectTrigger>
                      <SelectContent>{allowedBrands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Date d&apos;entrée</Label>
                    <Input type="date" value={form.date_entree} onChange={(e) => setForm({ ...form, date_entree: e.target.value })} data-testid="eq-date" />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Quantité</Label>
                    <Input type="number" min="1" value={form.quantite} onChange={(e) => setForm({ ...form, quantite: e.target.value })} data-testid="eq-qty" />
                  </div>
                </div>
                <div className="flex items-center justify-between border border-zinc-200 rounded-sm p-3 bg-zinc-50/50">
                  <div>
                    <Label className="text-sm font-medium text-zinc-900 mb-0.5 block">Alertes de stock bas</Label>
                    <p className="text-xs text-zinc-500 font-mono">
                      Active une alerte rouge dès que le stock disponible est ≤ 2.
                    </p>
                  </div>
                  <Switch
                    checked={form.alert_enabled}
                    onCheckedChange={(v) => setForm({ ...form, alert_enabled: v })}
                    data-testid="eq-alert-toggle"
                  />
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Signature contrôleur ({user?.name})</Label>
                  <SignaturePad onSave={(url) => setForm((f) => ({ ...f, controller_signature: url }))} value={form.controller_signature} />
                </div>
              </div>
              <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={submit} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="eq-save-btn">Enregistrer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <HorizontalScrollContainer>
        <table className="w-full min-w-[1200px] bg-white border border-zinc-200 rounded-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left label-uppercase p-3">Désignation</th>
              <th className="text-left label-uppercase p-3">Réf.</th>
              <th className="text-left label-uppercase p-3">Caractéristiques</th>
              <th className="text-left label-uppercase p-3">État</th>
              <th className="text-left label-uppercase p-3">Département</th>
              <th className="text-left label-uppercase p-3">Date</th>
              <th className="text-left label-uppercase p-3">Qté</th>
              <th className="text-left label-uppercase p-3">Dispo</th>
              <th className="text-left label-uppercase p-3">Contrôleur</th>
              <th className="text-left label-uppercase p-3">Signature</th>
              {canAssign && <th className="text-right label-uppercase p-3">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => {
              const b = brands.find((x) => x.id === e.brand_id);
              const s = stockMap[e.id];
              const dispo = s?.disponible ?? 0;
              const isLow = dispo <= 2 && (e.alert_enabled !== false);
              return (
                <tr key={e.id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${isLow ? "bg-red-50/40" : ""}`} data-testid={`eq-row-${e.id}`}>
                  <td className="p-3 text-sm font-medium text-zinc-900">
                    <span className="inline-flex items-center gap-1.5">
                      <Package size={14} className="text-amber-700 shrink-0" data-testid={`eq-icon-${e.id}`} />
                      {isLow && <AlertTriangle size={13} className="text-red-600 shrink-0" data-testid={`eq-warn-${e.id}`} />}
                      {e.alert_enabled === false && <BellOff size={11} className="text-zinc-400 shrink-0" title="Alertes désactivées" />}
                      {e.designation}
                    </span>
                  </td>
                  <td className="p-3 text-xs font-mono text-zinc-700">{e.ref_id}</td>
                  <td className="p-3 text-sm text-zinc-600 max-w-xs truncate">{e.caracteristiques || "—"}</td>
                  <td className="p-3"><EtatBadge value={e.etat} /></td>
                  <td className="p-3 text-sm text-zinc-700">{b?.name || "—"}</td>
                  <td className="p-3 text-xs font-mono text-zinc-600">
                    <div>{formatDateFr(e.date_entree)}</div>
                    {e.created_at && (
                      <div className="text-[10px] text-zinc-400 mt-0.5" data-testid={`eq-hour-${e.id}`}>
                        {formatTimeFr(e.created_at)}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-sm font-semibold text-zinc-900">{e.quantite}</td>
                  <td className="p-3 text-sm font-bold">
                    <span className={isLow ? "text-red-600" : "text-emerald-700"} data-testid={`eq-dispo-${e.id}`}>
                      {dispo}
                    </span>
                  </td>
                  <td className="p-3 text-sm text-zinc-700">{e.controller_name || "—"}</td>
                  <td className="p-3"><SignatureViewer url={e.controller_signature} label="Signature contrôleur" /></td>
                  {canAssign && (
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="outline" size="sm" className="h-7 px-2"
                          onClick={() => setEditing(e)}
                          data-testid={`eq-edit-${e.id}`}
                          title="Modifier"
                        >
                          <Pencil size={12} />
                        </Button>
                        <Button
                          variant="outline" size="sm" className="h-7 px-2 text-sky-700 border-sky-200 hover:bg-sky-50"
                          onClick={() => setRestocking(e)}
                          data-testid={`eq-restock-${e.id}`}
                          title="Réapprovisionner"
                        >
                          <RefreshCw size={12} />
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="outline" size="sm" className="h-7 px-2 text-red-700 border-red-200 hover:bg-red-50"
                            onClick={() => remove(e.id)}
                            data-testid={`eq-del-${e.id}`}
                            title="Supprimer"
                          >
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={canAssign ? 11 : 10} className="text-center p-8 text-sm text-zinc-500 font-mono">
                {entries.length === 0 ? "Aucune entrée" : "Aucun résultat pour ces filtres"}
              </td></tr>
            )}
          </tbody>
        </table>
      </HorizontalScrollContainer>

      <EditEquipmentDialog
        key={editing?.id || "none"}
        entry={editing}
        brands={allowedBrands}
        user={user}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
      />
      <RestockDialog
        entry={restocking}
        user={user}
        stockMap={stockMap}
        onClose={() => setRestocking(null)}
        onSaved={() => { setRestocking(null); reload(); }}
      />
    </div>
  );
}

/* ---------------- Edit Equipment Dialog ---------------- */

function EditEquipmentDialog({ entry, brands, user, onClose, onSaved }) {
  const [form, setForm] = useState(() => entry ? {
    designation: entry.designation || "",
    ref_id: entry.ref_id || "",
    caracteristiques: entry.caracteristiques || "",
    etat: entry.etat || "Neuf",
    brand_id: entry.brand_id || "",
    date_entree: entry.date_entree || new Date().toISOString().slice(0, 10),
    quantite: entry.quantite ?? 1,
    alert_enabled: entry.alert_enabled !== false,
    controller_signature: null, // signature OBLIGATOIREMENT renouvelée à chaque modif
  } : null);

  if (!entry || !form) return null;

  const submit = async () => {
    if (!form.controller_signature) {
      toast.error("Veuillez apposer votre signature pour valider la modification");
      return;
    }
    try {
      await api.put(`/equipment/${entry.id}`, { ...form, quantite: Number(form.quantite) });
      toast.success("Équipement modifié");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-2xl" data-testid="edit-eq-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
          <DialogTitle>Modifier l&apos;équipement</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase mb-1.5 block">Désignation</Label>
              <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="edit-eq-designation" />
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">N° / Référence</Label>
              <Input value={form.ref_id} onChange={(e) => setForm({ ...form, ref_id: e.target.value })} data-testid="edit-eq-ref" />
            </div>
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Caractéristiques</Label>
            <Textarea rows={2} value={form.caracteristiques} onChange={(e) => setForm({ ...form, caracteristiques: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="label-uppercase mb-1.5 block">État</Label>
              <Select value={form.etat} onValueChange={(v) => setForm({ ...form, etat: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ETATS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">Département</Label>
              <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">Date d&apos;entrée</Label>
              <Input type="date" value={form.date_entree} onChange={(e) => setForm({ ...form, date_entree: e.target.value })} />
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">Quantité</Label>
              <Input type="number" min="1" value={form.quantite} onChange={(e) => setForm({ ...form, quantite: e.target.value })} data-testid="edit-eq-qty" />
            </div>
          </div>
          <div className="flex items-center justify-between border border-zinc-200 rounded-sm p-3 bg-zinc-50/50">
            <div>
              <Label className="text-sm font-medium text-zinc-900 mb-0.5 block">Alertes de stock bas</Label>
              <p className="text-xs text-zinc-500 font-mono">Active une alerte rouge dès que le stock disponible est ≤ 2.</p>
            </div>
            <Switch
              checked={form.alert_enabled}
              onCheckedChange={(v) => setForm({ ...form, alert_enabled: v })}
              data-testid="edit-eq-alert-toggle"
            />
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Re-signature contrôleur ({user?.name})</Label>
            <SignaturePad onSave={(url) => setForm((f) => ({ ...f, controller_signature: url }))} value={form.controller_signature} />
            <p className="text-[11px] text-zinc-500 font-mono mt-1">Une nouvelle signature est obligatoire pour valider la modification (audit log).</p>
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="edit-eq-save-btn">Valider la modification</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Restock Dialog ---------------- */

function RestockDialog({ entry, user, stockMap, onClose, onSaved }) {
  const [form, setForm] = useState({ quantite_ajout: 1, date_arrivage: new Date().toISOString().slice(0, 10), controller_signature: null });

  if (!entry) return null;
  const dispo = stockMap[entry.id]?.disponible ?? 0;

  const submit = async () => {
    if (!form.controller_signature) {
      toast.error("Signature contrôleur requise");
      return;
    }
    if (Number(form.quantite_ajout) < 1) {
      toast.error("Quantité à ajouter invalide");
      return;
    }
    try {
      await api.post(`/equipment/${entry.id}/restock`, { ...form, quantite_ajout: Number(form.quantite_ajout) });
      toast.success(`+${form.quantite_ajout} unité(s) ajoutée(s) au stock`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg" data-testid="restock-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
          <DialogTitle>Réapprovisionner — {entry.designation}</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
          <div className="bg-sky-50 border border-sky-200 rounded-sm p-3 text-xs font-mono text-sky-900">
            Réf. : <b>{entry.ref_id}</b><br />
            Stock actuel : <b>{entry.quantite}</b> · Disponible : <b>{dispo}</b>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase mb-1.5 block">Quantité à ajouter</Label>
              <Input
                type="number" min="1" value={form.quantite_ajout}
                onChange={(e) => setForm({ ...form, quantite_ajout: e.target.value })}
                data-testid="restock-qty"
              />
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">Date d&apos;arrivage</Label>
              <Input type="date" value={form.date_arrivage} onChange={(e) => setForm({ ...form, date_arrivage: e.target.value })} data-testid="restock-date" />
            </div>
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Signature contrôleur ({user?.name})</Label>
            <SignaturePad onSave={(url) => setForm((f) => ({ ...f, controller_signature: url }))} value={form.controller_signature} />
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} className="bg-sky-600 hover:bg-sky-700" data-testid="restock-save-btn">
            <RefreshCw size={12} className="mr-1" /> Réapprovisionner
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Site Assignments Tab ---------------- */

function SiteAssignmentsTab({ items, entries, stock, stockMap, brands, sites, collabs, user, isAdmin, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [etatFilter, setEtatFilter] = useState("");
  const [pendingReceptionOnly, setPendingReceptionOnly] = useState(false);
  const [form, setForm] = useState({
    equipment_id: "", brand_id: "", site_id: "", etat: "Bon état",
    quantite_sortie: 1,
    date_sortie: new Date().toISOString().slice(0, 10),
    heure_sortie: new Date().toTimeString().slice(0, 5),
    delivery_mode: "",
    receiver_collaborator_id: "",
    controller_signature: null,
  });
  const selectedEq = entries.find((e) => e.id === form.equipment_id);
  const selectedStock = stock.find((s) => s.id === form.equipment_id);
  const maxAvailable = selectedStock?.disponible ?? 0;
  const availableBrands = user?.role === "admin" ? brands : brands.filter((b) => (user?.brand_ids || []).includes(b.id));
  const availableSites = sites.filter((s) => (s.brand_ids || []).includes(form.brand_id));
  // Liste des réceptionnaires possibles : tous les utilisateurs actifs, sauf le contrôleur lui-même
  const availableReceivers = useMemo(() => {
    return (collabs || [])
      .filter((c) => c.is_active !== false && c.id !== user?.id)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [collabs, user?.id]);

  const resetForm = () => setForm({
    equipment_id: "", brand_id: "", site_id: "", etat: "Bon état",
    quantite_sortie: 1,
    date_sortie: new Date().toISOString().slice(0, 10),
    heure_sortie: new Date().toTimeString().slice(0, 5),
    delivery_mode: "",
    receiver_collaborator_id: "",
    controller_signature: null,
  });

  const submit = async () => {
    if (!form.equipment_id || !form.brand_id || !form.site_id) {
      toast.error("Matériel, département et site requis");
      return;
    }
    if (!form.delivery_mode) {
      toast.error("Veuillez sélectionner « Immédiatement sur site » ou « À acheminer sur site »");
      return;
    }
    if (form.delivery_mode === "a_acheminer_sur_site" && !form.receiver_collaborator_id) {
      toast.error("Sélectionnez un collaborateur réceptionnaire pour le mode « à acheminer sur site »");
      return;
    }
    if (!form.controller_signature) {
      toast.error("Signature contrôleur requise");
      return;
    }
    const qty = Number(form.quantite_sortie);
    if (qty < 1) {
      toast.error("Quantité invalide");
      return;
    }
    if (qty > maxAvailable) {
      toast.error(`Quantité supérieure au stock disponible (${maxAvailable})`);
      return;
    }
    try {
      await api.post("/equipment-site-assignments", { ...form, quantite_sortie: qty });
      toast.success("Affectation enregistrée");
      setOpen(false);
      resetForm();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette affectation ?")) return;
    try {
      await api.delete(`/equipment-site-assignments/${id}`);
      toast.success("Supprimée");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // Détection « en attente de réception » : mode acheminement + non encore confirmé par le réceptionnaire
  const isPendingReception = (a) =>
    a.delivery_mode === "a_acheminer_sur_site"
    && a.status === "en_cours_affectation"
    && !a.received_at;

  const pendingReceptionCount = useMemo(
    () => items.filter(isPendingReception).length,
    [items],
  );

  // Filtrage
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return items.filter((a) => {
      if (pendingReceptionOnly && !isPendingReception(a)) return false;
      if (brandFilter && a.brand_id !== brandFilter) return false;
      if (etatFilter && a.etat !== etatFilter) return false;
      if (!q) return true;
      const site = sites.find((s) => s.id === a.site_id);
      return (
        (a.designation_snapshot || "").toLowerCase().includes(q)
        || (a.ref_id_snapshot || "").toLowerCase().includes(q)
        || (a.caracteristiques_snapshot || "").toLowerCase().includes(q)
        || (site?.name || "").toLowerCase().includes(q)
      );
    });
  }, [items, search, brandFilter, etatFilter, sites, pendingReceptionOnly]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltersBar
          search={search} onSearchChange={setSearch}
          brand={brandFilter} onBrandChange={setBrandFilter} brands={availableBrands}
          etat={etatFilter} onEtatChange={setEtatFilter}
          testIdPrefix="sites"
        />
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant={pendingReceptionOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setPendingReceptionOnly((v) => !v)}
            className={`rounded-sm shrink-0 ${
              pendingReceptionOnly
                ? "bg-amber-500 hover:bg-amber-600 text-white border-amber-500"
                : "border-amber-300 text-amber-800 hover:bg-amber-50"
            }`}
            data-testid="sites-pending-reception-toggle"
            title="Afficher uniquement les matériels en attente de réception"
          >
            <Truck size={14} className="mr-1.5" />
            En attente de réception
            <span
              className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-mono font-semibold ${
                pendingReceptionOnly
                  ? "bg-white text-amber-700"
                  : "bg-amber-100 text-amber-800"
              }`}
              data-testid="sites-pending-reception-count"
            >
              {pendingReceptionCount}
            </span>
          </Button>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm shrink-0" data-testid="new-site-assign-btn">
              <Plus size={14} className="mr-1" /> Nouvelle affectation site
            </Button>
          </DialogTrigger>
          <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-2xl">
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
              <DialogTitle>Affectation site</DialogTitle>
            </DialogHeader>
            <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
              <div>
                <Label className="label-uppercase mb-1.5 block">Matériel (entrée stock)</Label>
                <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v, quantite_sortie: 1 })}>
                  <SelectTrigger data-testid="sa-eq"><SelectValue placeholder="Sélectionner un matériel" /></SelectTrigger>
                  <SelectContent>
                    {entries.map((e) => {
                      const s = stockMap[e.id];
                      return (
                        <SelectItem key={e.id} value={e.id} data-testid={`sa-eq-opt-${e.id}`} disabled={(s?.disponible ?? 0) <= 0}>
                          {e.designation} ({e.ref_id}) — dispo: {s?.disponible ?? "?"}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {selectedEq && (
                  <p className="text-xs font-mono text-zinc-500 mt-1">
                    Caractéristiques : {selectedEq.caracteristiques || "—"} · Stock dispo : <b className={maxAvailable <= 0 ? "text-red-600" : ""}>{maxAvailable}</b>
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="label-uppercase mb-1.5 block">État sortie</Label>
                  <Select value={form.etat} onValueChange={(v) => setForm({ ...form, etat: v })}>
                    <SelectTrigger data-testid="sa-etat"><SelectValue /></SelectTrigger>
                    <SelectContent>{ETATS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Département</Label>
                  <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v, site_id: "" })}>
                    <SelectTrigger data-testid="sa-brand"><SelectValue placeholder="…" /></SelectTrigger>
                    <SelectContent>{availableBrands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Site</Label>
                  <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })} disabled={!form.brand_id}>
                    <SelectTrigger data-testid="sa-site"><SelectValue placeholder="…" /></SelectTrigger>
                    <SelectContent>
                      {availableSites.length === 0 && <SelectItem value="__empty" disabled>Aucun site pour ce département</SelectItem>}
                      {availableSites.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`sa-site-opt-${s.id}`}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">
                    Quantité sortie {selectedEq && <span className="text-zinc-400 font-normal">(max {maxAvailable})</span>}
                  </Label>
                  <Input
                    type="number" min="1" max={maxAvailable || undefined}
                    value={form.quantite_sortie}
                    onChange={(e) => setForm({ ...form, quantite_sortie: e.target.value })}
                    data-testid="sa-qty"
                    className={Number(form.quantite_sortie) > maxAvailable ? "border-red-400" : ""}
                  />
                  {selectedEq && Number(form.quantite_sortie) > maxAvailable && (
                    <p className="text-[11px] text-red-600 mt-0.5">Dépasse le stock disponible ({maxAvailable}).</p>
                  )}
                </div>
                <div className="sm:col-span-1">
                  <Label className="label-uppercase mb-1.5 block">Date de sortie</Label>
                  <Input type="date" value={form.date_sortie} onChange={(e) => setForm({ ...form, date_sortie: e.target.value })} data-testid="sa-date" />
                </div>
                <div className="sm:col-span-1">
                  <Label className="label-uppercase mb-1.5 block">Heure de sortie</Label>
                  <Input type="time" value={form.heure_sortie || ""} onChange={(e) => setForm({ ...form, heure_sortie: e.target.value })} data-testid="sa-heure" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="label-uppercase mb-1.5 block">
                    Mode de mise à disposition <span className="text-red-600">*</span>
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {Object.entries(DELIVERY_MODE_LABELS).map(([val, label]) => {
                      const selected = form.delivery_mode === val;
                      return (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setForm({ ...form, delivery_mode: val, receiver_collaborator_id: val === "a_acheminer_sur_site" ? form.receiver_collaborator_id : "" })}
                          data-testid={`sa-delivery-${val}`}
                          className={`flex items-center gap-2 border rounded-sm px-3 py-2.5 text-sm text-left transition-colors ${
                            selected
                              ? "border-[#0055FF] bg-sky-50 text-[#0044CC] font-medium"
                              : "border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700"
                          }`}
                        >
                          <span
                            className={`inline-flex items-center justify-center w-4 h-4 rounded-full border ${
                              selected ? "border-[#0055FF]" : "border-zinc-300"
                            }`}
                          >
                            {selected && <span className="w-2 h-2 rounded-full bg-[#0055FF]" />}
                          </span>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {form.delivery_mode === "a_acheminer_sur_site" && (
                  <div className="sm:col-span-2">
                    <Label className="label-uppercase mb-1.5 block">
                      Collaborateur réceptionnaire <span className="text-red-600">*</span>
                    </Label>
                    <Select
                      value={form.receiver_collaborator_id}
                      onValueChange={(v) => setForm({ ...form, receiver_collaborator_id: v })}
                    >
                      <SelectTrigger data-testid="sa-receiver"><SelectValue placeholder="Sélectionner un réceptionnaire" /></SelectTrigger>
                      <SelectContent>
                        {availableReceivers.length === 0 && <SelectItem value="__empty" disabled>Aucun collaborateur disponible</SelectItem>}
                        {availableReceivers.map((c) => (
                          <SelectItem key={c.id} value={c.id} data-testid={`sa-receiver-opt-${c.id}`}>
                            {c.name || c.email} <span className="text-zinc-400 text-xs ml-1">({c.role})</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-zinc-500 font-mono mt-1">Ce collaborateur sera notifié et devra confirmer la réception (signature).</p>
                  </div>
                )}
              </div>
              <div>
                <Label className="label-uppercase mb-1.5 block">Signature contrôleur ({user?.name})</Label>
                <SignaturePad onSave={(url) => setForm((f) => ({ ...f, controller_signature: url }))} value={form.controller_signature} />
              </div>
            </div>
            <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button
                onClick={submit}
                className="bg-[#0055FF] hover:bg-[#0044CC]"
                disabled={(!!selectedEq && Number(form.quantite_sortie) > maxAvailable) || !form.delivery_mode || (form.delivery_mode === "a_acheminer_sur_site" && !form.receiver_collaborator_id)}
                data-testid="sa-save-btn"
              >Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <HorizontalScrollContainer>
        <table className="w-full min-w-[1500px] bg-white border border-zinc-200 rounded-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left label-uppercase p-3">Désignation</th>
              <th className="text-left label-uppercase p-3">Réf.</th>
              <th className="text-left label-uppercase p-3">Caractéristiques</th>
              <th className="text-left label-uppercase p-3">État</th>
              <th className="text-left label-uppercase p-3">Département</th>
              <th className="text-left label-uppercase p-3">Site</th>
              <th className="text-left label-uppercase p-3">Mise à dispo.</th>
              <th className="text-left label-uppercase p-3">Qté sortie</th>
              <th className="text-left label-uppercase p-3">Date sortie</th>
              <th className="text-left label-uppercase p-3">Heure sortie</th>
              <th className="text-left label-uppercase p-3">Contrôleur</th>
              <th className="text-left label-uppercase p-3">Signature</th>
              <th className="text-left label-uppercase p-3">Réceptionnaire</th>
              <th className="text-left label-uppercase p-3">Réception</th>
              <th className="text-left label-uppercase p-3">Sign. réceptionnaire</th>
              <th className="text-right label-uppercase p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => {
              const b = brands.find((x) => x.id === a.brand_id);
              const s = sites.find((x) => x.id === a.site_id);
              return (
                <tr key={a.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`sa-row-${a.id}`}>
                  <td className="p-3 text-sm font-medium">{a.designation_snapshot || "—"}</td>
                  <td className="p-3 text-xs font-mono">{a.ref_id_snapshot || "—"}</td>
                  <td className="p-3 text-sm text-zinc-600 max-w-xs truncate">{a.caracteristiques_snapshot || "—"}</td>
                  <td className="p-3"><EtatBadge value={a.etat} /></td>
                  <td className="p-3 text-sm">{b?.name || "—"}</td>
                  <td className="p-3 text-sm">{s?.name || "—"}</td>
                  <td className="p-3 text-xs">
                    {a.delivery_mode ? (
                      <div className="flex flex-col gap-1">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border font-mono w-fit ${
                            a.delivery_mode === "presence_sur_site"
                              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                              : "bg-amber-50 border-amber-200 text-amber-700"
                          }`}
                          data-testid={`sa-mode-${a.id}`}
                        >
                          {DELIVERY_MODE_LABELS[a.delivery_mode] || a.delivery_mode}
                        </span>
                        {a.delivery_mode === "a_acheminer_sur_site" && (
                          isPendingReception(a) ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border bg-amber-100 border-amber-300 text-amber-900 font-mono w-fit"
                              data-testid={`sa-pending-${a.id}`}
                              title="En attente de réception par le collaborateur"
                            >
                              <Truck size={10} /> En attente
                            </span>
                          ) : a.received_at ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border bg-emerald-50 border-emerald-200 text-emerald-700 font-mono w-fit"
                              data-testid={`sa-received-${a.id}`}
                              title={`Réceptionné le ${formatDateFr(a.received_at)}`}
                            >
                              <CheckCircle2 size={10} /> Réceptionné
                            </span>
                          ) : null
                        )}
                      </div>
                    ) : (
                      <span className="text-zinc-400 font-mono">—</span>
                    )}
                  </td>
                  <td className="p-3 text-sm font-semibold">{a.quantite_sortie}</td>
                  <td className="p-3 text-xs font-mono">{formatDateFr(a.date_sortie)}</td>
                  <td className="p-3 text-xs font-mono">{formatTimeFr(a.heure_sortie) || <span className="text-zinc-400">—</span>}</td>
                  <td className="p-3 text-sm">{a.controller_name || "—"}</td>
                  <td className="p-3"><SignatureViewer url={a.controller_signature} label="Signature contrôleur" /></td>
                  <td className="p-3 text-sm" data-testid={`sa-receiver-${a.id}`}>
                    {a.delivery_mode === "a_acheminer_sur_site"
                      ? (a.receiver_name
                          || collabs.find((c) => c.id === a.receiver_id)?.name
                          || collabs.find((c) => c.id === a.receiver_id)?.email
                          || <span className="text-zinc-400 font-mono">—</span>)
                      : <span className="text-zinc-400 font-mono">N/A</span>}
                  </td>
                  <td className="p-3 text-xs font-mono" data-testid={`sa-reception-at-${a.id}`}>
                    {a.received_at
                      ? formatDateTimeFr(a.received_at)
                      : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="p-3" data-testid={`sa-receiver-sig-${a.id}`}>
                    {a.delivery_mode === "a_acheminer_sur_site"
                      ? <SignatureViewer url={a.receiver_signature} label="Signature réceptionnaire" />
                      : <span className="text-zinc-400 font-mono">—</span>}
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="outline" size="sm" className="h-7 px-2"
                        onClick={() => setEditing(a)}
                        data-testid={`sa-edit-${a.id}`}
                        title="Modifier"
                      >
                        <Pencil size={12} />
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="outline" size="sm" className="h-7 px-2 text-red-700 border-red-200 hover:bg-red-50"
                          onClick={() => remove(a.id)}
                          data-testid={`sa-del-${a.id}`}
                          title="Supprimer"
                        >
                          <Trash2 size={12} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={16} className="text-center p-8 text-sm text-zinc-500 font-mono">
                {items.length === 0 ? "Aucune affectation site" : "Aucun résultat pour ces filtres"}
              </td></tr>
            )}
          </tbody>
        </table>
      </HorizontalScrollContainer>

      <EditSiteAssignmentDialog
        key={editing?.id || "none"}
        assignment={editing}
        sites={sites}
        stock={stock}
        collabs={collabs}
        user={user}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
      />
    </div>
  );
}

/* ---------------- Collab Assignments Tab ---------------- */

function CollabAssignmentsTab({ items, entries, stock, stockMap, collabs, brands, user, isAdmin, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [etatFilter, setEtatFilter] = useState("");
  const [form, setForm] = useState({
    equipment_id: "", collaborateur_id: "", etat: "Bon état",
    quantite: 1, date_affectation: new Date().toISOString().slice(0, 10),
    heure_affectation: new Date().toTimeString().slice(0, 5),
    delivery_mode: "",
    controller_signature: null,
  });
  const [searchCollab, setSearchCollab] = useState("");
  const selectedEq = entries.find((e) => e.id === form.equipment_id);
  const selectedStock = stock.find((s) => s.id === form.equipment_id);
  const maxAvailable = selectedStock?.disponible ?? 0;
  const filteredCollabs = useMemo(() => {
    const q = searchCollab.toLowerCase().trim();
    const list = collabs.filter((c) => c.role !== "admin");
    if (!q) return list;
    return list.filter((c) => (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q));
  }, [collabs, searchCollab]);

  const availableBrands = user?.role === "admin" ? brands : brands.filter((b) => (user?.brand_ids || []).includes(b.id));

  const resetForm = () => setForm({
    equipment_id: "", collaborateur_id: "", etat: "Bon état",
    quantite: 1, date_affectation: new Date().toISOString().slice(0, 10),
    controller_signature: null,
  });

  const submit = async () => {
    if (!form.equipment_id || !form.collaborateur_id) {
      toast.error("Matériel et collaborateur requis");
      return;
    }
    if (!form.delivery_mode) {
      toast.error("Veuillez sélectionner « Remis en main propre » ou « Acheminement livraison »");
      return;
    }
    if (!form.controller_signature) {
      toast.error("Signature contrôleur requise");
      return;
    }
    const qty = Number(form.quantite);
    if (qty < 1) { toast.error("Quantité invalide"); return; }
    if (qty > maxAvailable) {
      toast.error(`Quantité supérieure au stock disponible (${maxAvailable})`);
      return;
    }
    try {
      await api.post("/equipment-collab-assignments", { ...form, quantite: qty });
      toast.success("Affectation enregistrée, notification envoyée au collaborateur");
      setOpen(false);
      resetForm();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette affectation ?")) return;
    try {
      await api.delete(`/equipment-collab-assignments/${id}`);
      toast.success("Supprimée");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // Filtrage — les affectations dont la restitution est en cours (collab a signé)
  // ou déjà finalisée (admin a contre-signé) ne doivent PAS rester dans
  // « Affectation collaborateurs » : elles sont gérées dans l'onglet
  // « Restitution de matériels ».
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return items.filter((a) => {
      if (a.status === "returned" || a.status === "awaiting_return_controller") return false;
      const collab = collabs.find((c) => c.id === a.collaborateur_id);
      if (brandFilter) {
        // On filtre via la brand de l'équipement source
        const eq = entries.find((e) => e.id === a.equipment_id);
        if (eq && eq.brand_id !== brandFilter) return false;
      }
      if (etatFilter && a.etat !== etatFilter) return false;
      if (!q) return true;
      return (
        (a.designation_snapshot || "").toLowerCase().includes(q)
        || (a.ref_id_snapshot || "").toLowerCase().includes(q)
        || (collab?.name || "").toLowerCase().includes(q)
        || (collab?.email || "").toLowerCase().includes(q)
      );
    });
  }, [items, search, brandFilter, etatFilter, collabs, entries]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltersBar
          search={search} onSearchChange={setSearch}
          brand={brandFilter} onBrandChange={setBrandFilter} brands={availableBrands}
          etat={etatFilter} onEtatChange={setEtatFilter}
          testIdPrefix="collabs"
        />
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm shrink-0" data-testid="new-collab-assign-btn">
              <Plus size={14} className="mr-1" /> Nouvelle affectation collaborateur
            </Button>
          </DialogTrigger>
          <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-2xl">
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
              <DialogTitle>Affectation collaborateur</DialogTitle>
            </DialogHeader>
            <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
              <div>
                <Label className="label-uppercase mb-1.5 block">Matériel</Label>
                <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v, quantite: 1 })}>
                  <SelectTrigger data-testid="ca-eq"><SelectValue placeholder="Sélectionner un matériel" /></SelectTrigger>
                  <SelectContent>
                    {entries.map((e) => {
                      const s = stockMap[e.id];
                      return (
                        <SelectItem key={e.id} value={e.id} data-testid={`ca-eq-opt-${e.id}`} disabled={(s?.disponible ?? 0) <= 0}>
                          {e.designation} ({e.ref_id}) — dispo: {s?.disponible ?? "?"}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {selectedEq && (
                  <p className="text-xs font-mono text-zinc-500 mt-1">
                    Stock dispo : <b className={maxAvailable <= 0 ? "text-red-600" : ""}>{maxAvailable}</b>
                  </p>
                )}
              </div>
              <div>
                <Label className="label-uppercase mb-1.5 block">Recherche collaborateur</Label>
                <Input placeholder="Nom ou email…" value={searchCollab} onChange={(e) => setSearchCollab(e.target.value)} data-testid="ca-collab-search" />
              </div>
              <div>
                <Label className="label-uppercase mb-1.5 block">Collaborateur bénéficiaire</Label>
                <Select value={form.collaborateur_id} onValueChange={(v) => setForm({ ...form, collaborateur_id: v })}>
                  <SelectTrigger data-testid="ca-collab"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {filteredCollabs.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`ca-collab-opt-${c.id}`}>{c.name} · {c.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <Label className="label-uppercase mb-1.5 block">État</Label>
                  <Select value={form.etat} onValueChange={(v) => setForm({ ...form, etat: v })}>
                    <SelectTrigger data-testid="ca-etat"><SelectValue /></SelectTrigger>
                    <SelectContent>{ETATS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">
                    Quantité {selectedEq && <span className="text-zinc-400 font-normal">(max {maxAvailable})</span>}
                  </Label>
                  <Input
                    type="number" min="1" max={maxAvailable || undefined}
                    value={form.quantite}
                    onChange={(e) => setForm({ ...form, quantite: e.target.value })}
                    data-testid="ca-qty"
                    className={Number(form.quantite) > maxAvailable ? "border-red-400" : ""}
                  />
                  {selectedEq && Number(form.quantite) > maxAvailable && (
                    <p className="text-[11px] text-red-600 mt-0.5">Dépasse le stock dispo ({maxAvailable}).</p>
                  )}
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Date</Label>
                  <Input type="date" value={form.date_affectation} onChange={(e) => setForm({ ...form, date_affectation: e.target.value })} data-testid="ca-date" />
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Heure</Label>
                  <Input type="time" value={form.heure_affectation || ""} onChange={(e) => setForm({ ...form, heure_affectation: e.target.value })} data-testid="ca-heure" />
                </div>
              </div>
              <div>
                <Label className="label-uppercase mb-1.5 block">
                  Mode de mise à disposition <span className="text-red-600">*</span>
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.entries(COLLAB_DELIVERY_MODE_LABELS).map(([val, label]) => {
                    const selected = form.delivery_mode === val;
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setForm({ ...form, delivery_mode: val })}
                        data-testid={`ca-delivery-${val}`}
                        className={`flex items-center gap-2 border rounded-sm px-3 py-2.5 text-sm text-left transition-colors ${
                          selected
                            ? "border-[#0055FF] bg-sky-50 text-[#0044CC] font-medium"
                            : "border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700"
                        }`}
                      >
                        <span
                          className={`inline-flex items-center justify-center w-4 h-4 rounded-full border ${
                            selected ? "border-[#0055FF]" : "border-zinc-300"
                          }`}
                        >
                          {selected && <span className="w-2 h-2 rounded-full bg-[#0055FF]" />}
                        </span>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="label-uppercase mb-1.5 block">Signature contrôleur ({user?.name})</Label>
                <SignaturePad onSave={(url) => setForm((f) => ({ ...f, controller_signature: url }))} value={form.controller_signature} />
              </div>
              <p className="text-xs font-mono text-zinc-500">
                Le collaborateur recevra une notification + demande de confirmation de réception (signature).
              </p>
            </div>
            <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button
                onClick={submit}
                className="bg-[#0055FF] hover:bg-[#0044CC]"
                disabled={(!!selectedEq && Number(form.quantite) > maxAvailable) || !form.delivery_mode}
                data-testid="ca-save-btn"
              >Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <HorizontalScrollContainer>
        <table className="w-full min-w-[1500px] bg-white border border-zinc-200 rounded-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left label-uppercase p-3">Désignation</th>
              <th className="text-left label-uppercase p-3">Réf.</th>
              <th className="text-left label-uppercase p-3">Caractéristiques</th>
              <th className="text-left label-uppercase p-3">État</th>
              <th className="text-left label-uppercase p-3">Mode mise à dispo.</th>
              <th className="text-left label-uppercase p-3">Collaborateur</th>
              <th className="text-left label-uppercase p-3">Qté</th>
              <th className="text-left label-uppercase p-3">Date</th>
              <th className="text-left label-uppercase p-3">Contrôleur</th>
              <th className="text-left label-uppercase p-3">Sign. ctrl</th>
              <th className="text-left label-uppercase p-3">Confirmation</th>
              <th className="text-left label-uppercase p-3">Heure conf.</th>
              <th className="text-right label-uppercase p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => {
              const c = collabs.find((x) => x.id === a.collaborateur_id);
              return (
                <tr key={a.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`ca-row-${a.id}`}>
                  <td className="p-3 text-sm font-medium">{a.designation_snapshot || "—"}</td>
                  <td className="p-3 text-xs font-mono">{a.ref_id_snapshot || "—"}</td>
                  <td className="p-3 text-sm text-zinc-600 max-w-xs truncate">{a.caracteristiques_snapshot || "—"}</td>
                  <td className="p-3"><EtatBadge value={a.etat} /></td>
                  <td className="p-3">
                    {a.delivery_mode ? (
                      <span className="text-[10px] uppercase font-semibold px-2 py-1 border rounded-sm tracking-wider bg-zinc-50 border-zinc-200 text-zinc-700">
                        {COLLAB_DELIVERY_MODE_LABELS[a.delivery_mode] || a.delivery_mode}
                      </span>
                    ) : (
                      <span className="text-zinc-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="p-3 text-sm">{c?.name || "—"}</td>
                  <td className="p-3 text-sm font-semibold">{a.quantite}</td>
                  <td className="p-3 text-xs font-mono whitespace-nowrap">
                    {formatDateFr(a.date_affectation)}
                    {a.heure_affectation && (
                      <span className="ml-1 text-zinc-500">{formatTimeFr(a.heure_affectation)}</span>
                    )}
                  </td>
                  <td className="p-3 text-sm">{a.controller_name || "—"}</td>
                  <td className="p-3"><SignatureViewer url={a.controller_signature} label="Signature contrôleur" /></td>
                  <td className="p-3">
                    {a.status === "awaiting_return_controller" ? (
                      <span
                        className="text-[10px] uppercase font-semibold px-2 py-1 border rounded-sm tracking-wider bg-sky-50 border-sky-200 text-sky-700"
                        title="Le collaborateur a signé la restitution — en attente de contre-signature du contrôleur."
                        data-testid={`ca-status-awaiting-return-${a.id}`}
                      >
                        Restitution à contre-signer
                      </span>
                    ) : a.status === "confirmed" ? (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase font-semibold px-2 py-1 border rounded-sm tracking-wider bg-emerald-50 border-emerald-200 text-emerald-700">
                          Confirmé
                        </span>
                        <SignatureViewer url={a.collab_signature} label="Signature collaborateur" />
                      </div>
                    ) : (
                      <span className="text-[10px] uppercase font-semibold px-2 py-1 border rounded-sm tracking-wider bg-amber-50 border-amber-200 text-amber-700">
                        En attente
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-xs font-mono whitespace-nowrap">
                    {a.confirmed_at ? (
                      <span className="text-zinc-700">{formatTimeFr(a.confirmed_at)}</span>
                    ) : (
                      <span className="text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="outline" size="sm" className="h-7 px-2"
                        onClick={() => setEditing(a)}
                        data-testid={`ca-edit-${a.id}`}
                        title={a.status === "awaiting_return_controller"
                          ? "Modification verrouillée : restitution en cours de validation"
                          : "Modifier"}
                        disabled={a.status === "awaiting_return_controller"}
                      >
                        <Pencil size={12} />
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="outline" size="sm" className="h-7 px-2 text-red-700 border-red-200 hover:bg-red-50"
                          onClick={() => remove(a.id)}
                          data-testid={`ca-del-${a.id}`}
                          title={a.status === "awaiting_return_controller"
                            ? "Suppression verrouillée : restitution en cours de validation"
                            : "Supprimer"}
                          disabled={a.status === "awaiting_return_controller"}
                        >
                          <Trash2 size={12} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={13} className="text-center p-8 text-sm text-zinc-500 font-mono">
                {items.length === 0 ? "Aucune affectation collaborateur" : "Aucun résultat pour ces filtres"}
              </td></tr>
            )}
          </tbody>
        </table>
      </HorizontalScrollContainer>

      <EditCollabAssignmentDialog
        key={editing?.id || "none"}
        assignment={editing}
        collabs={collabs}
        stock={stock}
        user={user}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
      />
    </div>
  );
}

/* ---------------- Edit Site Assignment Dialog ---------------- */

function EditSiteAssignmentDialog({ assignment, sites, stock, collabs, user, onClose, onSaved }) {
  const [form, setForm] = useState(() => assignment ? {
    site_id: assignment.site_id || "",
    etat: assignment.etat || "Bon état",
    quantite_sortie: assignment.quantite_sortie ?? 1,
    date_sortie: assignment.date_sortie || new Date().toISOString().slice(0, 10),
    heure_sortie: assignment.heure_sortie || "",
    delivery_mode: assignment.delivery_mode || "",
    receiver_collaborator_id: assignment.receiver_id || assignment.receiver_collaborator_id || "",
    controller_signature: null,
  } : null);

  const availableReceivers = useMemo(() => {
    return (collabs || [])
      .filter((c) => c.is_active !== false && c.id !== user?.id)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [collabs, user?.id]);

  if (!assignment || !form) return null;

  // Sites limités au département de l'affectation
  const availableSites = sites.filter((s) => (s.brand_ids || []).includes(assignment.brand_id));
  const eqStock = stock.find((s) => s.id === assignment.equipment_id);
  // Stock max = disponible + quantité déjà sortie sur cette ligne
  const maxAvailable = (eqStock?.disponible ?? 0) + Number(assignment.quantite_sortie || 0);
  const qty = Number(form.quantite_sortie);
  const overLimit = qty > maxAvailable;

  const submit = async () => {
    if (!form.controller_signature) {
      toast.error("Veuillez apposer votre signature pour valider la modification");
      return;
    }
    if (!form.delivery_mode) {
      toast.error("Veuillez sélectionner « Immédiatement sur site » ou « À acheminer sur site »");
      return;
    }
    if (form.delivery_mode === "a_acheminer_sur_site" && !form.receiver_collaborator_id) {
      toast.error("Sélectionnez un collaborateur réceptionnaire pour le mode « à acheminer sur site »");
      return;
    }
    if (qty < 1) {
      toast.error("Quantité minimale : 1");
      return;
    }
    if (overLimit) {
      toast.error(`Quantité supérieure au stock disponible (${maxAvailable})`);
      return;
    }
    try {
      await api.put(`/equipment-site-assignments/${assignment.id}`, { ...form, quantite_sortie: qty });
      toast.success("Affectation site modifiée");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-2xl" data-testid="edit-sa-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
          <DialogTitle>Modifier l&apos;affectation site</DialogTitle>
          <DialogDescription className="sr-only">
            Modification d&apos;une affectation site — re-signature contrôleur obligatoire.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
          <div className="bg-sky-50 border border-sky-200 rounded-sm p-3 text-xs font-mono text-sky-900">
            Matériel : <b>{assignment.designation_snapshot}</b> ({assignment.ref_id_snapshot})<br />
            Stock max modifiable : <b>{maxAvailable}</b>
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Site</Label>
            <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
              <SelectTrigger data-testid="edit-sa-site"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableSites.length === 0 && <SelectItem value="__empty" disabled>Aucun site pour ce département</SelectItem>}
                {availableSites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="label-uppercase mb-1.5 block">État</Label>
              <Select value={form.etat} onValueChange={(v) => setForm({ ...form, etat: v })}>
                <SelectTrigger data-testid="edit-sa-etat"><SelectValue /></SelectTrigger>
                <SelectContent>{ETATS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">
                Quantité sortie <span className="text-zinc-400 font-normal">(max {maxAvailable})</span>
              </Label>
              <Input
                type="number" min="1" max={maxAvailable || undefined}
                value={form.quantite_sortie}
                onChange={(e) => setForm({ ...form, quantite_sortie: e.target.value })}
                data-testid="edit-sa-qty"
                className={overLimit ? "border-red-400" : ""}
              />
              {overLimit && <p className="text-[11px] text-red-600 mt-0.5">Dépasse le stock disponible ({maxAvailable}).</p>}
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">Date de sortie</Label>
              <Input type="date" value={form.date_sortie} onChange={(e) => setForm({ ...form, date_sortie: e.target.value })} data-testid="edit-sa-date" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="label-uppercase mb-1.5 block">Heure de sortie</Label>
              <Input type="time" value={form.heure_sortie || ""} onChange={(e) => setForm({ ...form, heure_sortie: e.target.value })} data-testid="edit-sa-heure" />
            </div>
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">
              Mode de mise à disposition <span className="text-red-600">*</span>
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(DELIVERY_MODE_LABELS).map(([val, label]) => {
                const selected = form.delivery_mode === val;
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setForm({ ...form, delivery_mode: val, receiver_collaborator_id: val === "a_acheminer_sur_site" ? form.receiver_collaborator_id : "" })}
                    data-testid={`edit-sa-delivery-${val}`}
                    className={`flex items-center gap-2 border rounded-sm px-3 py-2.5 text-sm text-left transition-colors ${
                      selected
                        ? "border-[#0055FF] bg-sky-50 text-[#0044CC] font-medium"
                        : "border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700"
                    }`}
                  >
                    <span
                      className={`inline-flex items-center justify-center w-4 h-4 rounded-full border ${
                        selected ? "border-[#0055FF]" : "border-zinc-300"
                      }`}
                    >
                      {selected && <span className="w-2 h-2 rounded-full bg-[#0055FF]" />}
                    </span>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          {form.delivery_mode === "a_acheminer_sur_site" && (
            <div>
              <Label className="label-uppercase mb-1.5 block">
                Collaborateur réceptionnaire <span className="text-red-600">*</span>
              </Label>
              <Select
                value={form.receiver_collaborator_id}
                onValueChange={(v) => setForm({ ...form, receiver_collaborator_id: v })}
              >
                <SelectTrigger data-testid="edit-sa-receiver"><SelectValue placeholder="Sélectionner un réceptionnaire" /></SelectTrigger>
                <SelectContent>
                  {availableReceivers.length === 0 && <SelectItem value="__empty" disabled>Aucun collaborateur disponible</SelectItem>}
                  {availableReceivers.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`edit-sa-receiver-opt-${c.id}`}>
                      {c.name || c.email} <span className="text-zinc-400 text-xs ml-1">({c.role})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-500 font-mono mt-1">Ce collaborateur sera notifié et devra confirmer la réception (signature).</p>
            </div>
          )}
          <div>
            <Label className="label-uppercase mb-1.5 block">Re-signature contrôleur ({user?.name})</Label>
            <SignaturePad onSave={(url) => setForm((f) => ({ ...f, controller_signature: url }))} value={form.controller_signature} />
            <p className="text-[11px] text-zinc-500 font-mono mt-1">Une nouvelle signature est obligatoire pour valider la modification (audit log).</p>
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={overLimit || !form.controller_signature || !form.delivery_mode || (form.delivery_mode === "a_acheminer_sur_site" && !form.receiver_collaborator_id)} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="edit-sa-save-btn">
            Valider la modification
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Edit Collab Assignment Dialog ---------------- */

function EditCollabAssignmentDialog({ assignment, collabs, stock, user, onClose, onSaved }) {
  const [form, setForm] = useState(() => assignment ? {
    collaborateur_id: assignment.collaborateur_id || "",
    etat: assignment.etat || "Bon état",
    quantite: assignment.quantite ?? 1,
    date_affectation: assignment.date_affectation || new Date().toISOString().slice(0, 10),
    controller_signature: null,
  } : null);
  const [searchCollab, setSearchCollab] = useState("");

  if (!assignment || !form) return null;

  // Note: useMemo must stay before any conditional return → déplacé en dehors.
  const filteredCollabs = filterCollabsHelper(collabs, searchCollab);

  const eqStock = stock.find((s) => s.id === assignment.equipment_id);
  const maxAvailable = (eqStock?.disponible ?? 0) + Number(assignment.quantite || 0);
  const qty = Number(form.quantite);
  const overLimit = qty > maxAvailable;
  const isConfirmed = assignment.status === "confirmed";

  const submit = async () => {
    if (!form.controller_signature) {
      toast.error("Veuillez apposer votre signature pour valider la modification");
      return;
    }
    if (qty < 1) {
      toast.error("Quantité minimale : 1");
      return;
    }
    if (overLimit) {
      toast.error(`Quantité supérieure au stock disponible (${maxAvailable})`);
      return;
    }
    try {
      await api.put(`/equipment-collab-assignments/${assignment.id}`, { ...form, quantite: qty });
      toast.success("Affectation collaborateur modifiée");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-2xl" data-testid="edit-ca-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
          <DialogTitle>Modifier l&apos;affectation collaborateur</DialogTitle>
          <DialogDescription className="sr-only">
            Modification d&apos;une affectation collaborateur — re-signature contrôleur obligatoire.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
          <div className="bg-sky-50 border border-sky-200 rounded-sm p-3 text-xs font-mono text-sky-900">
            Matériel : <b>{assignment.designation_snapshot}</b> ({assignment.ref_id_snapshot})<br />
            Stock max modifiable : <b>{maxAvailable}</b>
            {isConfirmed && (
              <><br /><span className="text-amber-800">⚠ Cette affectation a déjà été confirmée par le collaborateur. La modification réinitialisera la confirmation côté backend si la signature collaborateur n&apos;est pas re-validée.</span></>
            )}
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Recherche collaborateur</Label>
            <Input placeholder="Nom ou email…" value={searchCollab} onChange={(e) => setSearchCollab(e.target.value)} data-testid="edit-ca-collab-search" />
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Collaborateur bénéficiaire</Label>
            <Select value={form.collaborateur_id} onValueChange={(v) => setForm({ ...form, collaborateur_id: v })}>
              <SelectTrigger data-testid="edit-ca-collab"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
              <SelectContent>
                {filteredCollabs.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} · {c.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="label-uppercase mb-1.5 block">État</Label>
              <Select value={form.etat} onValueChange={(v) => setForm({ ...form, etat: v })}>
                <SelectTrigger data-testid="edit-ca-etat"><SelectValue /></SelectTrigger>
                <SelectContent>{ETATS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">
                Quantité <span className="text-zinc-400 font-normal">(max {maxAvailable})</span>
              </Label>
              <Input
                type="number" min="1" max={maxAvailable || undefined}
                value={form.quantite}
                onChange={(e) => setForm({ ...form, quantite: e.target.value })}
                data-testid="edit-ca-qty"
                className={overLimit ? "border-red-400" : ""}
              />
              {overLimit && <p className="text-[11px] text-red-600 mt-0.5">Dépasse le stock disponible ({maxAvailable}).</p>}
            </div>
            <div>
              <Label className="label-uppercase mb-1.5 block">Date</Label>
              <Input type="date" value={form.date_affectation} onChange={(e) => setForm({ ...form, date_affectation: e.target.value })} data-testid="edit-ca-date" />
            </div>
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Re-signature contrôleur ({user?.name})</Label>
            <SignaturePad onSave={(url) => setForm((f) => ({ ...f, controller_signature: url }))} value={form.controller_signature} />
            <p className="text-[11px] text-zinc-500 font-mono mt-1">Une nouvelle signature est obligatoire pour valider la modification (audit log).</p>
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={overLimit || !form.controller_signature} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="edit-ca-save-btn">
            Valider la modification
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Stock Tab ---------------- */

function StockTab({ stock, brands }) {
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [etatFilter, setEtatFilter] = useState("");

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return stock.filter((s) => {
      if (brandFilter && s.brand_id !== brandFilter) return false;
      if (etatFilter && s.etat !== etatFilter) return false;
      if (!q) return true;
      return (
        (s.designation || "").toLowerCase().includes(q)
        || (s.ref_id || "").toLowerCase().includes(q)
        || (s.caracteristiques || "").toLowerCase().includes(q)
      );
    });
  }, [stock, search, brandFilter, etatFilter]);

  return (
    <div className="space-y-4">
      <FiltersBar
        search={search} onSearchChange={setSearch}
        brand={brandFilter} onBrandChange={setBrandFilter} brands={brands}
        etat={etatFilter} onEtatChange={setEtatFilter}
        testIdPrefix="stock"
      />
      <HorizontalScrollContainer>
        <table className="w-full min-w-[1000px] bg-white border border-zinc-200 rounded-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left label-uppercase p-3">Désignation</th>
              <th className="text-left label-uppercase p-3">Réf.</th>
              <th className="text-left label-uppercase p-3">Caractéristiques</th>
              <th className="text-left label-uppercase p-3">État</th>
              <th className="text-left label-uppercase p-3">Département</th>
              <th className="text-left label-uppercase p-3">Date entrée</th>
              <th className="text-left label-uppercase p-3">Qté entrée</th>
              <th className="text-left label-uppercase p-3">Sortie site</th>
              <th className="text-left label-uppercase p-3">Sortie collab.</th>
              <th className="text-left label-uppercase p-3">Disponible</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => {
              const b = brands.find((x) => x.id === s.brand_id);
              const isLow = s.disponible <= 2 && (s.alert_enabled !== false);
              const isOut = s.disponible <= 0;
              return (
                <tr key={s.id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${isLow ? "bg-red-50/40" : ""}`} data-testid={`stock-row-${s.id}`}>
                  <td className="p-3 text-sm font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      <Package size={14} className="text-amber-700 shrink-0" data-testid={`stock-icon-${s.id}`} />
                      {isLow && <AlertTriangle size={13} className="text-red-600 shrink-0" />}
                      {s.alert_enabled === false && <BellOff size={11} className="text-zinc-400 shrink-0" title="Alertes désactivées" />}
                      {s.designation}
                    </span>
                  </td>
                  <td className="p-3 text-xs font-mono">{s.ref_id}</td>
                  <td className="p-3 text-sm text-zinc-600 max-w-xs truncate">{s.caracteristiques || "—"}</td>
                  <td className="p-3"><EtatBadge value={s.etat} /></td>
                  <td className="p-3 text-sm">{b?.name || "—"}</td>
                  <td className="p-3 text-xs font-mono">{formatDateFr(s.date_entree)}</td>
                  <td className="p-3 text-sm">{s.quantite}</td>
                  <td className="p-3 text-sm text-zinc-600">{s.sortie_site}</td>
                  <td className="p-3 text-sm text-zinc-600">{s.sortie_collab}</td>
                  <td className="p-3">
                    <span className={`text-sm font-bold ${isOut ? "text-red-600" : isLow ? "text-red-600" : "text-emerald-700"}`} data-testid={`stock-dispo-${s.id}`}>
                      {s.disponible}
                    </span>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center p-8 text-sm text-zinc-500 font-mono">
                {stock.length === 0 ? "Aucun matériel en stock" : "Aucun résultat pour ces filtres"}
              </td></tr>
            )}
          </tbody>
        </table>
      </HorizontalScrollContainer>
    </div>
  );
}

/* ---------------- Alerts Tab ---------------- */

function AlertsTab({ alerts, brands }) {
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return alerts.filter((a) => {
      if (brandFilter && a.brand_id !== brandFilter) return false;
      if (!q) return true;
      return (
        (a.designation || "").toLowerCase().includes(q)
        || (a.ref_id || "").toLowerCase().includes(q)
        || (a.caracteristiques || "").toLowerCase().includes(q)
      );
    });
  }, [alerts, search, brandFilter]);

  return (
    <div className="space-y-4">
      <div className="bg-red-50 border border-red-200 rounded-sm p-4 flex items-start gap-3" data-testid="alerts-banner">
        <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-red-800">Stock critique — Réapprovisionnement requis</h3>
          <p className="text-xs text-red-700 font-mono mt-1">
            Les équipements listés ont un stock disponible ≤ 2 (et leurs alertes sont activées).
            Pour exclure un matériel de cette alerte, désactivez « Alertes de stock bas » dans sa fiche.
          </p>
        </div>
      </div>

      <FiltersBar
        search={search} onSearchChange={setSearch}
        brand={brandFilter} onBrandChange={setBrandFilter} brands={brands}
        testIdPrefix="alerts"
      />

      <HorizontalScrollContainer>
        <table className="w-full min-w-[1000px] bg-white border border-red-200 rounded-sm">
          <thead>
            <tr className="border-b border-red-200 bg-red-50/60">
              <th className="text-left label-uppercase p-3">Désignation</th>
              <th className="text-left label-uppercase p-3">Réf.</th>
              <th className="text-left label-uppercase p-3">Caractéristiques</th>
              <th className="text-left label-uppercase p-3">État</th>
              <th className="text-left label-uppercase p-3">Département</th>
              <th className="text-left label-uppercase p-3">Qté entrée</th>
              <th className="text-left label-uppercase p-3">Sorties</th>
              <th className="text-left label-uppercase p-3">Disponible</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => {
              const b = brands.find((x) => x.id === a.brand_id);
              const isOut = a.disponible <= 0;
              return (
                <tr key={a.id} className="border-b border-red-100 hover:bg-red-50/50" data-testid={`alert-row-${a.id}`}>
                  <td className="p-3 text-sm font-medium text-zinc-900">
                    <span className="inline-flex items-center gap-1.5">
                      <Package size={14} className="text-amber-700 shrink-0" data-testid={`alert-icon-${a.id}`} />
                      <AlertTriangle size={13} className="text-red-600 shrink-0" />
                      {a.designation}
                    </span>
                  </td>
                  <td className="p-3 text-xs font-mono">{a.ref_id}</td>
                  <td className="p-3 text-sm text-zinc-600 max-w-xs truncate">{a.caracteristiques || "—"}</td>
                  <td className="p-3"><EtatBadge value={a.etat} /></td>
                  <td className="p-3 text-sm">{b?.name || "—"}</td>
                  <td className="p-3 text-sm">{a.quantite}</td>
                  <td className="p-3 text-sm text-zinc-600">{a.total_sorti}</td>
                  <td className="p-3">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-bold border ${
                        isOut ? "bg-red-100 border-red-300 text-red-800" : "bg-amber-50 border-amber-300 text-amber-800"
                      }`}
                      data-testid={`alert-dispo-${a.id}`}
                    >
                      {isOut ? "Rupture" : `${a.disponible} restant${a.disponible > 1 ? "s" : ""}`}
                    </span>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center p-8 text-sm text-zinc-500 font-mono">
                {alerts.length === 0 ? "Aucune alerte de stock" : "Aucun résultat pour ces filtres"}
              </td></tr>
            )}
          </tbody>
        </table>
      </HorizontalScrollContainer>
    </div>
  );
}
